import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import AppButton from '../../../../components/designe/app-button/AppButton';
import { useGetLanguages } from '../../../../services/redux/api/languagesAPI';
import { usePreviewNotificationMutation } from '../../../../services/redux/api/notificationsAdminApi';

type Props = {
  open: boolean;
  onClose: () => void;
  templateCode: string;
};

export function NotificationPreviewModal(props: Props) {
  const { open, onClose, templateCode } = props;
  const { t } = useTranslation();
  const { data: languages = [] } = useGetLanguages();
  const [preview, { isLoading }] = usePreviewNotificationMutation();

  const [languageId, setLanguageId] = useState('');
  const [userId, setUserId] = useState('');
  const [variablesJson, setVariablesJson] = useState('{}');
  const [resultTitle, setResultTitle] = useState('');
  const [resultBody, setResultBody] = useState('');

  const runPreview = async () => {
    let variables: Record<string, unknown> | undefined;
    try {
      const parsed = JSON.parse(variablesJson || '{}');
      variables =
        parsed && typeof parsed === 'object' && !Array.isArray(parsed)
          ? (parsed as Record<string, unknown>)
          : {};
    } catch {
      return;
    }
    const res = await preview({
      templateCode,
      languageId: languageId || undefined,
      userId: userId.trim() || undefined,
      variables,
    }).unwrap();
    setResultTitle(res.title);
    setResultBody(res.body);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
        <Typography component="span" variant="h6" sx={{ flex: 1 }}>
          {t('adminDashboard.notifications.preview.title')}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('adminDashboard.notifications.preview.templateCode')}:{' '}
          <strong>{templateCode}</strong>
        </Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="plang">{t('common.language')}</InputLabel>
          <Select
            labelId="plang"
            label={t('common.language')}
            value={languageId}
            onChange={(e) => setLanguageId(e.target.value as string)}>
            <MenuItem value="">
              <em>{t('adminDashboard.notifications.preview.autoLanguage')}</em>
            </MenuItem>
            {languages
              .filter((l) => l.id)
              .map((l) => (
                <MenuItem key={l.id} value={l.id}>
                  {l.title || l.key}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <TextField
          label={t('adminDashboard.notifications.preview.userId')}
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
          helperText={t('adminDashboard.notifications.preview.userIdHint')}
        />
        <TextField
          label={t('adminDashboard.notifications.preview.variablesJson')}
          value={variablesJson}
          onChange={(e) => setVariablesJson(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          sx={{ mb: 2 }}
        />
        <AppButton onClick={runPreview} loader={isLoading} sx={{ mb: 2 }}>
          {t('adminDashboard.notifications.preview.run')}
        </AppButton>
        {(resultTitle || resultBody) && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">{t('common.title')}</Typography>
            <Typography variant="body2" paragraph>
              {resultTitle}
            </Typography>
            <Typography variant="subtitle2">
              {t('common.description')}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {resultBody}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
